/**
 * ProjectSummary Component
 * 
 * This component displays the project summary/overview in the Project Details page.
 * It shows key project metrics, status, timeline, and important information.
 * 
 * @param {string} projectId - The ID of the project
 * @param {string} projectName - The name of the project
 */

import { useState } from 'react';
import { Row, Col, Badge, ProgressBar, Button, Card, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './Projectdashbord.css';

const ProjectSummary = ({ projectId, projectName }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');

    // Mock data - Replace with actual API call
    const projectData = {
        name: projectName || 'eCommerce Website',
        code: projectId || 'PRJ-2024-001',
        client: 'Netclues Technologies',
        manager: 'John Doe',
        department: 'Development',
        startDate: 'Jan 15, 2025',
        endDate: 'Mar 30, 2025',
        status: 'In Progress',
        priority: 'High',
        description: 'A comprehensive e-commerce platform with advanced features including user authentication, payment gateway integration, inventory management, and analytics dashboard. A comprehensive e-commerce platform with advanced features including user authentication, payment gateway integration, inventory management, and analytics dashboard. A comprehensive e-commerce platform with advanced features including user authentication, payment gateway integration, inventory management, and analytics dashboard.'
    };

    const handleEditDescription = () => {
        setEditedDescription(projectData.description);
        setShowEditModal(true);
    };

    const handleSaveDescription = () => {
        // TODO: Save description via API
        console.log('Saving description:', editedDescription);
        setShowEditModal(false);
    };

    const progressData = {
        total: 48,
        completed: 28,
        pending: 12,
        inProgress: 6,
        delayed: 2,
        percentage: 58
    };

    const timeData = {
        estimatedHours: 520,
        loggedHours: 312,
        percentage: 60
    };

    const teamMembers = [
        { name: 'John Doe', role: 'Project Manager', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' },
        { name: 'Jane Smith', role: 'Team Lead', avatar: 'https://randomuser.me/api/portraits/women/44.jpg', initials: 'JS' },
        { name: 'Mike Johnson', role: 'Developer', avatar: 'https://randomuser.me/api/portraits/men/45.jpg', initials: 'MJ' },
        { name: 'Sarah Wilson', role: 'Designer', avatar: 'https://randomuser.me/api/portraits/women/50.jpg', initials: 'SW' },
        { name: 'David Brown', role: 'QA Engineer', avatar: 'https://randomuser.me/api/portraits/men/52.jpg', initials: 'DB' }
    ];

    const recentActivities = [
        { id: 1, user: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', action: 'Created Project', description: 'Created project "eCommerce Website"', time: '2 hours ago' },
        { id: 2, user: 'Sarah Wilson', avatar: 'https://randomuser.me/api/portraits/women/50.jpg', action: 'Updated Project Name', description: 'Renamed project to "Full Website Overhaul"', time: '3 hours ago' },
        { id: 3, user: 'Mike Johnson', avatar: 'https://randomuser.me/api/portraits/men/45.jpg', action: 'Created Task', description: 'Created task "Homepage Design Mockup"', time: '5 hours ago' },
        { id: 4, user: 'Emily Davis', avatar: 'https://randomuser.me/api/portraits/women/68.jpg', action: 'Logged Time', description: 'Logged 3 hours on API Development', time: '6 hours ago' },
        { id: 5, user: 'David Brown', avatar: 'https://randomuser.me/api/portraits/men/52.jpg', action: 'Changed Task Status', description: 'Marked "Database Schema" as completed', time: '8 hours ago' }
    ];

    const priorityStats = [
        { priority: 'High', count: 15, color: 'danger' },
        { priority: 'Medium', count: 22, color: 'warning' },
        { priority: 'Low', count: 11, color: 'info' }
    ];

    const statusStats = [
        { status: 'Backlog', count: 8, color: '#6b7280' },
        { status: 'Planning', count: 6, color: '#3b82f6' },
        { status: 'In Progress', count: 12, color: '#f59e0b' },
        { status: 'Completed', count: 18, color: '#10b981' },
        { status: 'On Hold', count: 3, color: '#8b5cf6' },
        { status: 'Cancelled', count: 1, color: '#ef4444' }
    ];

    const recentFiles = [
        { name: 'design-mockups.zip', date: 'Dec 1, 2024', size: '4.2 MB' },
        { name: 'project-requirements.pdf', date: 'Nov 30, 2024', size: '1.8 MB' },
        { name: 'wireframes.fig', date: 'Nov 29, 2024', size: '2.5 MB' },
        { name: 'api-documentation.docx', date: 'Nov 28, 2024', size: '3.1 MB' }
    ];

    const upcomingDeadlines = [
        { task: 'Homepage Design Completion', dueDate: 'Dec 5, 2024', urgency: 'high', daysLeft: 3 },
        { task: 'API Integration Testing', dueDate: 'Dec 8, 2024', urgency: 'medium', daysLeft: 6 },
        { task: 'Mobile Responsive Design', dueDate: 'Dec 12, 2024', urgency: 'low', daysLeft: 10 }
    ];

    const getStatusColor = (status) => {
        const colors = {
            'Planning': 'secondary',
            'In Progress': 'primary',
            'On Hold': 'warning',
            'Completed': 'success',
            'Cancelled': 'danger'
        };
        return colors[status] || 'secondary';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'High': 'danger',
            'Medium': 'warning',
            'Low': 'info'
        };
        return colors[priority] || 'secondary';
    };

    const getUrgencyColor = (urgency) => {
        const colors = {
            'high': 'danger',
            'medium': 'warning',
            'low': 'info'
        };
        return colors[urgency] || 'secondary';
    };

    return (
        <>
            {/* Project Information Card */}
            <div className='c-card-1 mb-4'>
                <div className='c-card-header-1'>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>Project Information</div>
                        <div className="d-flex gap-2">
                            <span className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getStatusColor(projectData.status)}`}>
                                {projectData.status}
                            </span>
                            <span className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getPriorityColor(projectData.priority)}`}>
                                {projectData.priority} Priority
                            </span>
                        </div>
                    </div>
                </div>
                <div className='c-card-body-1'>
                    <Row className="g-4">
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Project Name</div>
                            <div className="text-dark fw-semibold info-value">{projectData.name}</div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Project Code</div>
                            <div className="text-dark fw-semibold info-value">{projectData.code}</div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Client</div>
                            <div className="text-dark fw-semibold info-value">{projectData.client}</div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Department</div>
                            <div className="text-dark fw-semibold info-value">{projectData.department}</div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Project Manager</div>
                            <div className="text-dark fw-semibold info-value">{projectData.manager}</div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Start Date</div>
                            <div className="text-dark fw-semibold info-value">{projectData.startDate}</div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">End Date</div>
                            <div className="text-dark fw-semibold info-value">{projectData.endDate}</div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Duration</div>
                            <div className="text-dark fw-semibold info-value">75 Days</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Key Metrics Row */}
            <Row className="g-3 mb-4">
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary flex-shrink-0 rounded-3 icon-box-48">
                            <i className="fas fa-tasks"></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="text-primary fw-bold mb-1 stat-value-large">{progressData.total}</div>
                            <div className="text-secondary small">Total Tasks</div>
                        </div>
                    </div>
                </Col>
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success flex-shrink-0 rounded-3 icon-box-48">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="text-success fw-bold mb-1 stat-value-large">{progressData.completed}</div>
                            <div className="text-secondary small">Completed Tasks</div>
                        </div>
                    </div>
                </Col>
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning flex-shrink-0 rounded-3 icon-box-48">
                            <i className="fas fa-spinner"></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="text-warning fw-bold mb-1 stat-value-large">{progressData.inProgress}</div>
                            <div className="text-secondary small">In Progress</div>
                        </div>
                    </div>
                </Col>
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 text-danger flex-shrink-0 rounded-3 icon-box-48">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="text-danger fw-bold mb-1 stat-value-large">{progressData.delayed}</div>
                            <div className="text-secondary small">Delayed Tasks</div>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="g-4 mb-4">
                {/* Progress Overview */}
                <Col lg={5}>
                    <div className='c-card-1 h-100 d-flex flex-column'>
                        <div className='c-card-header-1'>
                            <div>Progress Overview</div>
                        </div>
                        <div className='c-card-body-1 h-100 d-flex flex-column'>
                            <div className="text-center mb-4 flex-grow-1 d-flex align-items-center justify-content-center">
                                <div className="progress-circle-container">
                                    <svg width="220" height="220" viewBox="0 0 220 220">
                                        <circle cx="110" cy="110" r="90" fill="none" stroke="#f0f4f8" strokeWidth="14" />
                                        <circle 
                                            cx="110" 
                                            cy="110" 
                                            r="90" 
                                            fill="none" 
                                            stroke="url(#progressGradientNew)" 
                                            strokeWidth="14"
                                            strokeDasharray={`${2 * Math.PI * 90}`}
                                            strokeDashoffset={`${2 * Math.PI * 90 * (1 - progressData.percentage / 100)}`}
                                            strokeLinecap="round"
                                            transform="rotate(-90 110 110)"
                                        />
                                        <defs>
                                            <linearGradient id="progressGradientNew" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#60a5fa" />
                                                <stop offset="100%" stopColor="#3b82f6" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <div className="progress-circle-text">
                                        <div className="fs-1 fw-bold mb-2 text-primary">{progressData.percentage}%</div>
                                        <div className="text-muted small">Complete</div>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex justify-content-around text-center pt-3 border-top">
                                <div>
                                    <div className="fw-bold fs-5 text-success">{progressData.completed}</div>
                                    <small className="text-muted">Done</small>
                                </div>
                                <div className="border-start"></div>
                                <div>
                                    <div className="fw-bold fs-5 text-warning">{progressData.inProgress}</div>
                                    <small className="text-muted">Active</small>
                                </div>
                                <div className="border-start"></div>
                                <div>
                                    <div className="fw-bold fs-5 text-secondary">{progressData.pending}</div>
                                    <small className="text-muted">Pending</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Time Overview */}
                <Col lg={7}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <div>Time Tracking</div>
                        </div>
                        <div className='c-card-body-1'>
                            <Row className="g-3 mb-4">
                                <Col sm={6}>
                                    <div className="bg-white border rounded-3 p-3 time-stat-card-hover">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div className="text-secondary small mb-2">Estimated Hours</div>
                                                <div className="text-dark fw-bold stat-value-large">{timeData.estimatedHours}h</div>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-center rounded-3 bg-light-gray icon-box-40">
                                                <i className="fas fa-hourglass-half text-secondary"></i>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={6}>
                                    <div className="border rounded-3 p-3 time-stat-card-hover bg-time-logged border-light-blue">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div className="text-secondary small mb-2">Logged Hours</div>
                                                <div className="text-primary fw-bold stat-value-large">{timeData.loggedHours}h</div>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-center rounded-3 bg-light-blue-alt icon-box-40">
                                                <i className="fas fa-business-time text-primary"></i>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="text-muted small">Time Utilization</span>
                                    <span className="fw-bold text-primary">{timeData.percentage}%</span>
                                </div>
                                <ProgressBar className="rounded-pill time-utilization-bar-bg">
                                    <ProgressBar 
                                        now={timeData.percentage} 
                                        className="rounded-pill time-utilization-fill"
                                    />
                                </ProgressBar>
                            </div>
                            <div>
                                <div className="text-muted mb-3 small fw-medium">Weekly Time Log</div>
                                <div className="time-chart-container d-flex align-items-end justify-content-between gap-2">
                                    {[42, 38, 45, 52, 48, 56, 50].map((hours, index) => (
                                        <div key={index} className="flex-fill position-relative h-100">
                                            <div 
                                                className="time-chart-bar-new w-100 position-absolute bottom-0"
                                                style={{ height: `${(hours / 60) * 100}%` }}
                                            >
                                                <span className="time-chart-value">{hours}h</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                                        <small key={index} className="text-muted flex-fill text-center">{day}</small>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="g-4 mb-4">
                {/* Team Overview */}
                <Col lg={4}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div>Team Members</div>
                                <Link 
                                    to="/projects/detail/teams" 
                                    className="text-primary text-decoration-none d-inline-flex align-items-center gap-2 view-all-link-hover text-md"
                                >
                                    View All Team
                                </Link>
                            </div>
                        </div>
                        <div className='c-card-body-1'>
                            <div className="d-flex flex-column gap-2">
                                {teamMembers.map((member, index) => (
                                    <div key={index} className="d-flex align-items-center gap-3 p-3 border rounded-3 team-member-card-hover">
                                        {member.avatar ? (
                                            <img 
                                                src={member.avatar} 
                                                alt={member.name}
                                                className="rounded-circle border border-2 border-white shadow-sm avatar-44"
                                            />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center rounded-circle border border-2 border-white shadow-sm text-white fw-semibold text-md bg-team-gradient avatar-44">
                                                {member.initials}
                                            </div>
                                        )}
                                        <div className="flex-grow-1 overflow-hidden">
                                            <Link 
                                                to="/projects/detail/teams" 
                                                className="text-decoration-none"
                                            >
                                                <div className="text-dark fw-semibold mb-1 text-base">{member.name}</div>
                                            </Link>
                                            <div className="text-secondary small">{member.role}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                </Col>

                {/* Latest Activities Preview */}
                <Col lg={8}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div>Recent Activities</div>
                                <Link 
                                    to="/projects/detail/activities" 
                                    className="text-primary text-decoration-none d-inline-flex align-items-center gap-2 view-all-link-hover text-md"
                                >
                                    View All Activities
                                </Link>
                            </div>
                        </div>
                        <div className='c-card-body-1'>
                            <div className="d-flex flex-column gap-2">
                                {recentActivities.map((activity) => (
                                    <div key={activity.id} className="d-flex align-items-center gap-3 p-3 border rounded-3 activity-item-hover">
                                        <img 
                                            src={activity.avatar} 
                                            alt={activity.user}
                                            className="rounded-circle flex-shrink-0 avatar-36"
                                        />
                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="small mb-1">
                                                <span className="fw-semibold text-dark">{activity.user}</span>
                                                <span className="text-muted mx-2">•</span>
                                                <span className="text-muted">{activity.action}</span>
                                            </div>
                                            <div className="text-secondary text-sm-plus">{activity.description}</div>
                                        </div>
                                        <div className="text-muted flex-shrink-0 text-nowrap info-label">{activity.time}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="g-4 mb-4">
                {/* Task Summary by Priority */}
                <Col lg={4}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <span>Tasks by Priority</span>
                        </div>
                        <div className='c-card-body-1'>
                            <div className="d-flex flex-column gap-3">
                                {priorityStats.map((stat, index) => (
                                    <div 
                                        key={index} 
                                        className={`priority-card-new priority-card-${stat.priority.toLowerCase()} rounded-3 p-3`}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <div className={`fw-semibold mb-1 priority-title-${stat.priority.toLowerCase()}`}>
                                                    {stat.priority} Priority
                                                </div>
                                                <small className="text-secondary">{stat.count} tasks assigned</small>
                                            </div>
                                            <div className={`fs-2 fw-bold priority-count-${stat.priority.toLowerCase()}`}>
                                                {stat.count}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Task Summary by Status */}
                <Col lg={8}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <span>Tasks by Status</span>
                        </div>
                        <div className='c-card-body-1'>
                            <Row className="g-3">
                                {statusStats.map((stat, index) => {
                                    const statusIcons = {
                                        'Backlog': 'fas fa-inbox',
                                        'Planning': 'fas fa-clipboard-list',
                                        'In Progress': 'fas fa-spinner',
                                        'Completed': 'fas fa-check-circle',
                                        'On Hold': 'fas fa-pause-circle',
                                        'Cancelled': 'fas fa-times-circle'
                                    };
                                    const statusLabels = {
                                        'Backlog': 'Pending review',
                                        'Planning': 'Being planned',
                                        'In Progress': 'Active work',
                                        'Completed': 'Successfully done',
                                        'On Hold': 'Temporarily paused',
                                        'Cancelled': 'Not proceeding'
                                    };
                                    return (
                                        <Col xs={6} md={4} key={index}>
                                            <div 
                                                className="border rounded-3 h-100 p-3 status-card-hover"
                                                style={{ 
                                                    backgroundColor: `${stat.color}08`,
                                                    borderColor: `${stat.color}30`
                                                }}
                                            >
                                                <div className="d-flex align-items-center justify-content-between mb-3">
                                                    <div 
                                                        className="d-flex align-items-center justify-content-center rounded-2"
                                                        style={{ 
                                                            width: '40px',
                                                            height: '40px',
                                                            backgroundColor: `${stat.color}20`,
                                                            color: stat.color
                                                        }}
                                                    >
                                                        <i className={statusIcons[stat.status]}></i>
                                                    </div>
                                                    <div className="fw-bold" style={{ fontSize: '28px', color: stat.color }}>
                                                        {stat.count}
                                                    </div>
                                                </div>
                                                <div className="fw-semibold text-dark mb-1">{stat.status}</div>
                                                <div className="text-muted small">{statusLabels[stat.status]}</div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="g-4 mb-4">
                {/* Files Overview */}
                <Col lg={4}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div>Project Files</div>
                                <Link 
                                    to="/projects/detail/files" 
                                    className="text-primary text-decoration-none d-inline-flex align-items-center gap-2 view-all-link-hover text-md"
                                >
                                    View All
                                </Link>
                            </div>
                        </div>
                        <div className='c-card-body-1'>
                            <div className="border rounded-3 mb-3 p-3 files-total-card">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="text-secondary small mb-2">Total Files Uploaded</div>
                                        <div className="fw-bold text-primary" style={{ fontSize: '32px' }}>24</div>
                                    </div>
                                    <div 
                                        className="d-flex align-items-center justify-content-center rounded-3"
                                        style={{
                                            width: '60px',
                                            height: '60px',
                                            backgroundColor: '#e3f2fd'
                                        }}
                                    >
                                        <i className="fas fa-folder-open fa-2x text-primary"></i>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                <div className="text-muted fw-semibold mb-1 text-uppercase text-sm-plus letter-spacing-wide">Recent Uploads</div>
                                {recentFiles.map((file, index) => (
                                    <div key={index} className="d-flex align-items-center gap-3 p-3 border rounded-3 file-item-hover">
                                        <div className="d-flex align-items-center justify-content-center rounded-2 text-primary flex-shrink-0 bg-light-file icon-box-36">
                                            <i className="fas fa-file"></i>
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden">
                                            <a href="#" className="text-decoration-none text-dark">
                                                <div className="fw-semibold text-truncate small">{file.name}</div>
                                            </a>
                                            <small className="text-muted info-label">{file.date} • {file.size}</small>
                                        </div>
                                        <div 
                                            className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle flex-shrink-0 file-download-icon cursor-pointer icon-box-32"
                                            title="Download file"
                                        >
                                            <i className="fas fa-arrow-down text-primary small"></i>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Upcoming Deadlines */}
                <Col lg={4}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <span>Upcoming Deadlines</span>
                        </div>
                        <div className='c-card-body-1'>
                            <div className="d-flex flex-column gap-3">
                                {upcomingDeadlines.map((item, index) => (
                                    <div key={index} className="p-3 border rounded-3 deadline-card-hover">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="fw-semibold flex-grow-1 lh-base small">
                                                {item.task}
                                            </div>
                                            <span 
                                                className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-xs modern-badge badge-${getUrgencyColor(item.urgency)} ms-2`}
                                            >
                                                {item.urgency.toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                            <div>
                                                <div className="text-muted text-uppercase info-label">Due Date</div>
                                                <div className="fw-semibold small">
                                                    {item.dueDate}
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <div className={`fw-bold fs-5 text-${getUrgencyColor(item.urgency)}`}>
                                                    {item.daysLeft}
                                                </div>
                                                <small className="text-muted">days left</small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Project Description/Notes */}
                <Col lg={4}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span>Project Description</span>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={handleEditDescription}
                                >
                                    Edit Description
                                </Button>
                            </div>
                        </div>
                        <div className='c-card-body-1'>
                            <p className="text-muted mb-0 lh-lg small">
                                {projectData.description}
                            </p>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Edit Description Modal */}
            <Modal 
                show={showEditModal} 
                onHide={() => setShowEditModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5 fw-semibold">
                        Edit Project Description
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form.Group>
                        <Form.Label className="small fw-medium mb-2">
                            Description
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={8}
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            placeholder="Enter project description..."
                            className="small lh-lg rounded-3 border-secondary"
                        />
                        <Form.Text className="text-muted text-sm-plus">
                            Provide a detailed description of the project, its goals, and key information.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-top p-3">
                    <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowEditModal(false)} 
                        className="rounded-3"
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm"
                        onClick={handleSaveDescription} 
                        className="rounded-3"
                    >
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ProjectSummary;
